import React, { useState, useRef, useEffect, useCallback } from 'react';
import { debounce } from 'lodash';
import { useLazyQuery } from '@apollo/client';
import getCoordinates from 'textarea-caret';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';

import { SEARCH_CHATROOM_PROFILES } from '../../../../http/apollo-queries';
import { Mention } from '../types';

type PropTypes = {
  mentions: Mention[];
  setMentions: React.Dispatch<React.SetStateAction<Mention[]>>;
  chatRoomId: string;
} & React.DetailedHTMLProps<React.TextareaHTMLAttributes<HTMLTextAreaElement>, HTMLTextAreaElement>;

const TaggableTextarea = React.forwardRef<HTMLTextAreaElement, PropTypes>(({ mentions, setMentions, chatRoomId, ...rest }, ref) => {
  const [coordinates, setCoordinates] = useState<{ top: number; left: number; height: number }>();
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const [items, setItems] = useState<Array<{ id: string; name: string; owner: string; }>>([]);
  const searchData = useRef<{ index: number; search: string; }>();
  const anchorRef = useRef<HTMLDivElement>(null);

  const [searchProfiles] = useLazyQuery(SEARCH_CHATROOM_PROFILES);

  const isOpen = Boolean(anchorEl && items.length);

  const handleTextInput = useCallback(debounce((text: string) => {
    const startingPoint = text.lastIndexOf('@');

    if (startingPoint === -1) {
      return;
    }

    const search = text.slice(startingPoint + 1).trim();

    if (!search) {
      return;
    }

    searchData.current = {
      index: startingPoint,
      search,
    };

    searchProfiles({
      variables: {
        match: search,
      }
    }).then((data) => {
      const items = data.data?.searchProfiles?.items;
      if (items?.length) {
        setItems(items);
      }
    });
  }, 500), []);

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleChoose = (id: string, name: string, owner: string) => {
    if (searchData.current && ref && name && id) {
      const searchIndex = searchData.current.index;
      const searchString = searchData.current.search;
      const currentVal = (ref as React.MutableRefObject<HTMLTextAreaElement>).current.value;

      const preText = searchIndex > 0 ? currentVal.slice(0, searchIndex) : '';
      const postText = currentVal.slice(searchIndex + searchString.length + 1);

      const replacedVal = preText + name + postText;

      setAnchorEl(null);

      (ref as React.MutableRefObject<HTMLTextAreaElement>).current.value = replacedVal;

      if (mentions.findIndex(({ id: profileId }) => id === profileId) === -1) {
        setMentions((prevState) => [
          ...prevState,
          {
            id,
            name,
            ownerId: owner,
          }]);
      }
    }
  };

  useEffect(() => {
    if (ref && coordinates) {
      setTimeout(() => (ref as React.MutableRefObject<HTMLElement>).current.focus(), 100);
    }
  }, [ref, coordinates])

  const anchorStyles = coordinates && {
    top: `calc(${coordinates.top}px + 1.5rem)`,
    left: `${coordinates.left}px`,
    height: `${coordinates.height}px`,
  };

  return (
    <div className="relative">
      <textarea
        {...rest}
        ref={ref}
        onInput={function (e) {
          const target = e.currentTarget;

          handleTextInput(target.value);

          if (target.value.charAt(target.value.length - 3) === '@') {
            setAnchorEl(anchorRef.current);
            setCoordinates(getCoordinates(target, target.selectionEnd));
          } else if (target.value.slice(0, target.selectionEnd - 1).indexOf('@') === -1) {
            setAnchorEl(null);
          }
        }}
      />
      <div ref={anchorRef} className="invisible absolute" style={anchorStyles} />
      <Menu
        id="demo-positioned-menu"
        aria-labelledby="demo-positioned-button"
        anchorEl={anchorEl}
        open={isOpen}
        onClose={handleClose}
        disableScrollLock
        disableEnforceFocus
        disableAutoFocus
        disableAutoFocusItem
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
      >
        {
          items.map(({ id, name, owner }) => (
            <MenuItem key={id} onClick={() => handleChoose(id, name, owner)}>{name}</MenuItem>
          ))
        }
      </Menu>
    </div>
  );
});

export default TaggableTextarea;
